import React, { useState } from 'react';
import { Tabs, Tab, Box, Button, CircularProgress, Snackbar, Alert, Modal } from '@mui/material';
import ContentPackTable from "./ContentPackTable";
import ActiveContentPackTable from "./ActiveContentPackTable";
import { useTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { useGetTenancyByIdQuery, useUpdateTenancyMutation } from "../../store/services/tenancy.service";
import {
    useGetContentPacksQuery,
    useGetContentPackAssignmentsQuery,
    useCreateContentPackAssignmentMutation,
    useUpdateContentPackAssignmentMutation,
    useDeleteContentPackAssignmentMutation
} from "../../store/services/university-service";
import ContentPackPreview from "./ContentPackPreview";
import AssignPackModal from "./AssignPackModal";
import { ModalProvider } from '../../components/Modal/ModalContext';
import ConfirmationDialog from "../../utils/confirmationDialog";





const ContentPacks = () => {
    const theme = useTheme();
    const { tenancyId } = useSelector((state) => state.tenancy);
    const [updateTenancy] = useUpdateTenancyMutation();
    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const [previewContentPackId, setPreviewContentPackId] = useState(false);
    const [coursePreviewOpen, setCoursePreviewOpen] = useState(false);

    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");

    const [legacyBannerDismissed, setLegacyBannerDismissed] = useState(false);
    const [isRemovingLegacy, setIsRemovingLegacy] = useState(false);
    const [removeLegacyDialogOpen, setRemoveLegacyDialogOpen] = useState(false);
    const [convertModalOpen, setConvertModalOpen] = useState(false);
    const [isConvertingLegacy, setIsConvertingLegacy] = useState(false);

    const [paginationModel, setPaginationModel] = useState({
        pageSize: 100,
        page: 0
    });


    const { data: tenancyData } = useGetTenancyByIdQuery({ tenancyId });

    const {
        data: contentPacks,
        isLoading: contentPacksLoading,
        error: contentPacksError,
        refetch: refetchContentPacks
    } = useGetContentPacksQuery({
        perPage: 100,
        page: 0,
        searchTerm: '',
        tenancyId: tenancyId
    });

    const {
        data: assignments,
        isLoading: assignmentsLoading,
        error: assignmentsError,
        refetch: refetchAssignments
    } = useGetContentPackAssignmentsQuery({ tenancyId });

    const [createAssignment, { isLoading: isCreatingAssignment }] = useCreateContentPackAssignmentMutation();
    const [updateAssignment, { isLoading: isUpdatingAssignment }] = useUpdateContentPackAssignmentMutation();
    const [deleteAssignment] = useDeleteContentPackAssignmentMutation();


    const hasLegacyTenancyPack = !!tenancyData?.contentPackId;
    const legacyPack = hasLegacyTenancyPack
        ? contentPacks?.find(p => p.internalId === tenancyData.contentPackId)
        : null;
    const legacyPackName = legacyPack?.contentPackName;

    const handleAssignConfirm = async (assignData) => {
        const selectedPack = contentPacks?.find(p => p.internalId === assignData.contentPackId);
        if (!selectedPack) return;

        try {
            await createAssignment({
                contentPackId: assignData.contentPackId,
                tenancyId: String(tenancyId),
                scope: assignData.grouping,
                scopeId: assignData.grouping === 'Tenancy' ? null : String(assignData.recipient?.id),
                refreshEnabled: assignData.annualRefresh,
                sendToExistingUsers: assignData.sendToExistingUsers
            }).unwrap();
            setSnackbarMessage("Content pack assigned successfully.");
            setSnackbarSeverity("success");
            setSnackbarOpen(true);
        } catch (error) {
            console.error("Error creating content pack assignment:", error);
            setSnackbarMessage("Error assigning content pack.");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        }
    };

    const handleEditAssignment = async (editData) => {
        try {
            await updateAssignment({
                id: editData.id,
                assignmentData: {
                    refreshEnabled: editData.annualRefresh
                }
            }).unwrap();
            setSnackbarMessage("Assignment updated successfully.");
            setSnackbarSeverity("success");
            setSnackbarOpen(true);
        } catch (error) {
            console.error("Error updating assignment:", error);
            setSnackbarMessage("Error updating assignment.");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        }
    };

    const handleDeleteAssignment = async (assignmentId) => {
        try {
            await deleteAssignment(assignmentId).unwrap();
            setSnackbarMessage("Assignment deleted successfully.");
            setSnackbarSeverity("success");
            setSnackbarOpen(true);
        } catch (error) {
            console.error("Error deleting assignment:", error);
            setSnackbarMessage("Error deleting assignment.");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        }
    };

    const handleConvertLegacyConfirm = async (assignData) => {
        setIsConvertingLegacy(true);
        try {
            // Clear legacy pack first to prevent duplicates if assignment creation fails
            await updateTenancy({
                data: { ...tenancyData, contentPackId: null },
                tenancyId: tenancyData.tenancyId
            }).unwrap();

            await createAssignment({
                contentPackId: assignData.contentPackId,
                tenancyId: String(tenancyId),
                scope: assignData.grouping,
                scopeId: assignData.grouping === 'Tenancy' ? null : String(assignData.recipient?.id),
                refreshEnabled: assignData.annualRefresh,
                sendToExistingUsers: assignData.sendToExistingUsers
            }).unwrap();

            setSnackbarMessage("Legacy content pack converted to assignment successfully.");
            setSnackbarSeverity("success");
            setSnackbarOpen(true);
        } catch (error) {
            console.error("Error converting legacy content pack:", error);
            setSnackbarMessage("Error converting legacy content pack. Please check assignments and try again.");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        } finally {
            setIsConvertingLegacy(false);
        }
    };


    const handleRemoveLegacyPack = async () => {
        if (!tenancyData?.contentPackId) return;

        setIsRemovingLegacy(true);
        try {
            await updateTenancy({
                data: { ...tenancyData, contentPackId: null },
                tenancyId: tenancyData.tenancyId
            }).unwrap();

            setSnackbarMessage("Legacy content pack removed successfully.");
            setSnackbarSeverity("success");
            setSnackbarOpen(true);
        } catch (error) {
            console.error("Error removing legacy content pack:", error);
            setSnackbarMessage("Error removing legacy content pack.");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        } finally {
            setIsRemovingLegacy(false);
        }
    };

    const handlePreview = (contentPackId) => {
        setPreviewContentPackId(contentPackId);
        setPreviewModalOpen(true);
    };

    const handleClosePreviewModal = () => {
        if (!coursePreviewOpen) {
            setPreviewModalOpen(false);
            setPreviewContentPackId(null);
        }
    };

    const [activeTab, setActiveTab] = useState(0);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    return (
        <>
            {/* Subrail Navigation */}
            <Box sx={{ mx: 2, mb: 2 }}>
                <Tabs value={activeTab} onChange={handleTabChange}>
                    <Tab label="Active" />
                    <Tab label="Packs" />
                </Tabs>
            </Box>

            {/* Active Tab Content */}
            {activeTab === 0 && (
                <Box sx={{ width: '100%' }}>
                    {hasLegacyTenancyPack && !legacyBannerDismissed && (
                        <Alert
                            severity={isConvertingLegacy ? "info" : "warning"}
                            sx={{ mb: 2, mt: 2, display: 'flex', alignItems: 'center' }}
                            action={
                                isConvertingLegacy ? (
                                    <CircularProgress size={24} />
                                ) : (
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button
                                            color="error"
                                            variant="outlined"
                                            size="small"
                                            onClick={() => setRemoveLegacyDialogOpen(true)}
                                            disabled={isRemovingLegacy}
                                            sx={{ whiteSpace: 'nowrap' }}
                                        >
                                            {isRemovingLegacy ? (
                                                <CircularProgress size={20} color="inherit" />
                                            ) : (
                                                'Remove'
                                            )}
                                        </Button>
                                        <Button
                                            color="warning"
                                            variant="contained"
                                            size="small"
                                            onClick={() => setConvertModalOpen(true)}
                                            disabled={isRemovingLegacy}
                                            sx={{ whiteSpace: 'nowrap' }}
                                        >
                                            Convert
                                        </Button>
                                    </Box>
                                )
                            }
                            onClose={isConvertingLegacy ? undefined : () => setLegacyBannerDismissed(true)}
                        >
                            {isConvertingLegacy
                                ? 'Converting legacy content pack...'
                                : <>You have a legacy content pack{legacyPackName ? <> (<strong>{legacyPackName}</strong>)</> : ''} assigned at the tenancy level. Click <strong>Convert</strong> to migrate it to the new assignment system.</>
                            }
                        </Alert>
                    )}
                    <ActiveContentPackTable
                        data={assignments || []}
                        isLoading={assignmentsLoading || contentPacksLoading}
                        error={assignmentsError}
                        paginationModel={paginationModel}
                        setPaginationModel={setPaginationModel}
                        contentPacks={contentPacks || []}
                        groups={tenancyData?.groups || []}
                        onAssignConfirm={handleAssignConfirm}
                        onEdit={handleEditAssignment}
                        onDelete={handleDeleteAssignment}
                        isCreating={isCreatingAssignment}
                        isUpdating={isUpdatingAssignment}
                    />
                </Box>
            )}

            {/* Packs Tab Content */}
            {activeTab === 1 && (
                <Box sx={{ width: '100%' }}>
                    <ContentPackTable
                        contentPackData={contentPacks || []}
                        isLoading={contentPacksLoading}
                        error={contentPacksError}
                        refetchContentPacks={refetchContentPacks}
                        paginationModel={paginationModel}
                        setPaginationModel={setPaginationModel}
                    />
                </Box>
            )}
            <ModalProvider>
                <Modal
                    open={previewModalOpen}
                    onClose={handleClosePreviewModal}
                    aria-labelledby="simulation-preview-title"
                    aria-describedby="simulation-preview-description"
                    sx={{
                        '& .MuiBackdrop-root': {
                            backgroundColor: 'rgba(0, 0, 0, 0.5)'
                        }
                    }}
                >
                    <Box sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        borderRadius: '8px',
                        width: '600px',
                        height: '90vh',
                        p: 2,
                        bgcolor: theme.palette.background.default,
                        boxShadow: 24,
                        maxHeight: '90vh',
                        overflow: 'hidden',
                        visibility: coursePreviewOpen ? 'hidden' : 'visible'
                    }}>
                        {previewModalOpen && previewContentPackId && (
                            <ContentPackPreview
                                contentPackId={previewContentPackId}
                                setCoursePreviewOpen={setCoursePreviewOpen}
                            />
                        )}
                    </Box>
                </Modal>
            </ModalProvider>
        <Snackbar
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            open={snackbarOpen}
            autoHideDuration={3000}
            onClose={() => setSnackbarOpen(false)}>
            <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} variant="filled"
                sx={{ width: "100%" }}>
                {snackbarMessage}
            </Alert>
            </Snackbar>
        <AssignPackModal
            open={convertModalOpen}
            onClose={() => setConvertModalOpen(false)}
            contentPacks={contentPacks || []}
            groups={tenancyData?.groups || []}
            onConfirm={handleConvertLegacyConfirm}
            isLoading={isCreatingAssignment}
            initialValues={hasLegacyTenancyPack ? {
                contentPackId: tenancyData.contentPackId,
                grouping: 'Tenancy'
            } : null}
        />
        <ConfirmationDialog
            open={removeLegacyDialogOpen}
            title="Remove legacy content pack"
            content={`Are you sure you want to remove the legacy content pack${legacyPackName ? ` "${legacyPackName}"` : ''}? New users will no longer receive this pack. This cannot be undone.`}
            onConfirm={() => {
                setRemoveLegacyDialogOpen(false);
                handleRemoveLegacyPack();
            }}
            onCancel={() => setRemoveLegacyDialogOpen(false)}
        />
        </>
    );
}

export default ContentPacks
